import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

const colorCodeToName = (colorCode) => {
  if (!colorCode || typeof colorCode !== 'string') return colorCode;
  
  const normalized = colorCode.trim().toUpperCase().replace(/\s+/g, '');
  
  const colorMap = {
    '#000000': 'Black', '#FFFFFF': 'White', '#FF0000': 'Red', '#00FF00': 'Green',
    '#0000FF': 'Blue', '#FFFF00': 'Yellow', '#FF00FF': 'Magenta', '#00FFFF': 'Cyan',
    '#FFA500': 'Orange', '#800080': 'Purple', '#FFC0CB': 'Pink', '#A52A2A': 'Brown',
    '#808080': 'Gray', '#C0C0C0': 'Silver', '#FFD700': 'Gold', '#000080': 'Navy',
    '#008000': 'Green', '#800000': 'Maroon', '#FF6347': 'Tomato', '#FF1493': 'Deep Pink',
    '#00CED1': 'Dark Turquoise', '#FF4500': 'Orange Red', '#2E8B57': 'Sea Green',
    '#4682B4': 'Steel Blue', '#D2691E': 'Chocolate', '#DC143C': 'Crimson',
    '#8B008B': 'Dark Magenta', '#B22222': 'Fire Brick', '#228B22': 'Forest Green',
    '#20B2AA': 'Light Sea Green', '#FF69B4': 'Hot Pink', '#CD5C5C': 'Indian Red',
    '#4B0082': 'Indigo', '#F0E68C': 'Khaki', '#E6E6FA': 'Lavender', '#FFF0F5': 'Lavender Blush',
    '#7CFC00': 'Lawn Green', '#FFFACD': 'Lemon Chiffon', '#ADD8E6': 'Light Blue',
    '#F08080': 'Light Coral', '#E0FFFF': 'Light Cyan', '#FAFAD2': 'Light Goldenrod Yellow',
    '#D3D3D3': 'Light Gray', '#90EE90': 'Light Green', '#FFB6C1': 'Light Pink',
    '#FFA07A': 'Light Salmon', '#20B2AA': 'Light Sea Green', '#87CEFA': 'Light Sky Blue',
    '#778899': 'Light Slate Gray', '#B0C4DE': 'Light Steel Blue', '#FFFFE0': 'Light Yellow',
    '#32CD32': 'Lime Green', '#FF00FF': 'Magenta', '#800000': 'Maroon', '#66CDAA': 'Medium Aquamarine',
    '#0000CD': 'Medium Blue', '#BA55D3': 'Medium Orchid', '#9370DB': 'Medium Purple',
    '#3CB371': 'Medium Sea Green', '#7B68EE': 'Medium Slate Blue', '#00FA9A': 'Medium Spring Green',
    '#48D1CC': 'Medium Turquoise', '#C71585': 'Medium Violet Red', '#191970': 'Midnight Blue',
    '#F5FFFA': 'Mint Cream', '#FFE4E1': 'Misty Rose', '#FFE4B5': 'Moccasin',
    '#FFDEAD': 'Navajo White', '#000080': 'Navy', '#FDF5E6': 'Old Lace', '#808000': 'Olive',
    '#6B8E23': 'Olive Drab', '#FFA500': 'Orange', '#FF4500': 'Orange Red', '#DA70D6': 'Orchid',
    '#EEE8AA': 'Pale Goldenrod', '#98FB98': 'Pale Green', '#AFEEEE': 'Pale Turquoise',
    '#DB7093': 'Pale Violet Red', '#FFEFD5': 'Papaya Whip', '#FFDAB9': 'Peach Puff',
    '#CD853F': 'Peru', '#FFC0CB': 'Pink', '#DDA0DD': 'Plum', '#B0E0E6': 'Powder Blue',
    '#800080': 'Purple', '#663399': 'Rebecca Purple', '#FF0000': 'Red', '#BC8F8F': 'Rosy Brown',
    '#4169E1': 'Royal Blue', '#8B4513': 'Saddle Brown', '#FA8072': 'Salmon', '#F4A460': 'Sandy Brown',
    '#2E8B57': 'Sea Green', '#FFF5EE': 'Sea Shell', '#A0522D': 'Sienna', '#C0C0C0': 'Silver',
    '#87CEEB': 'Sky Blue', '#6A5ACD': 'Slate Blue', '#708090': 'Slate Gray', '#FFFAFA': 'Snow',
    '#00FF7F': 'Spring Green', '#4682B4': 'Steel Blue', '#D2B48C': 'Tan', '#008080': 'Teal',
    '#D8BFD8': 'Thistle', '#FF6347': 'Tomato', '#40E0D0': 'Turquoise', '#EE82EE': 'Violet',
    '#F5DEB3': 'Wheat', '#FFFFFF': 'White', '#F5F5F5': 'White Smoke', '#FFFF00': 'Yellow',
    '#9ACD32': 'Yellow Green'
  };
  
  if (colorMap[normalized]) {
    return colorMap[normalized];
  }
  
  if (normalized.startsWith('#')) {
    return colorCode;
  }
  
  if (/^[0-9A-F]{6}$/i.test(normalized)) {
    const hexColor = '#' + normalized;
    return colorMap[hexColor] || colorCode;
  }
  
  return colorCode;
};

class SpecificationTable extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updateTable);
    this.variantsData = this.loadVariantsData();
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updateTable);
  }

  loadVariantsData() {
    const script = this.querySelector('script[data-variants-metafields]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error('Error parsing variants metafields:', e);
      return null;
    }
  }

  updateTable = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const variantId = event.detail.resource?.id;
    if (!variantId || !this.variantsData) return;

    if (!event.detail.resource) return;

    const variantIdNum = typeof variantId === 'string' ? parseInt(variantId, 10) : variantId;
    const variant = this.variantsData.variants.find(v => v.id === variantIdNum);
    if (!variant) return;

    const metafields = variant.metafields;
    const fields = ['sex', 'intended_audience', 'size', 'color'];

    fields.forEach(field => {
      const row = this.querySelector(`tr[data-spec="${field}"]`);
      if (!row) return;

      const valueCell = row.querySelector('.spec-value[data-variant-field]');
      if (!valueCell) return;

      const variantValue = metafields[field];
      const fieldKey = field === 'intended_audience' ? 'productIntendedAudience' : `product${field.charAt(0).toUpperCase() + field.slice(1)}`;
      const productValue = this.dataset[fieldKey];

      let displayValue = '';
      if (variantValue != null && variantValue !== '') {
        displayValue = variantValue;
      } else if (productValue != null && productValue !== '') {
        displayValue = productValue;
      }

      if (field === 'color' && displayValue) {
        const convertedColor = colorCodeToName(displayValue);
        const currentText = valueCell.textContent.trim();
        if (currentText !== convertedColor && currentText !== displayValue) {
          valueCell.textContent = convertedColor;
        }
      } else if (displayValue && valueCell.textContent.trim() !== displayValue) {
        valueCell.textContent = displayValue;
      }
    });
  };
}

if (!customElements.get('specification-table')) {
  customElements.define('specification-table', SpecificationTable);
}
